// Updated: Access Identity Integration - Dual Mode Authentication
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AppLayout } from './components/layout';
import WorkflowList from './pages/WorkflowList';
import WorkflowEditor from './pages/WorkflowEditor';
import Executions from './pages/Executions';
import Credentials from './pages/Credentials';
import KnowledgeBase from './pages/KnowledgeBase';
import DataTables from './pages/DataTables';
import Teams from './pages/Teams';

import Analytics from './pages/Analytics';
import Templates from './pages/Templates';
import LibraryRequests from './pages/LibraryRequests';
import Variables from './pages/Variables';
import ChatDemo from './pages/ChatDemo';
import EmbedChatPage from './pages/EmbedChatPage';
import Home from './pages/Home';

import { initializePropertySchemas } from './lib/property-schemas-init';
import { DragDropProvider } from './contexts/DragDropContext';
import { RealTimeProvider } from './contexts/RealTimeContext';
import { useWorkflowStore } from './stores/workflow-store';
import { ErrorBoundary } from './components/error/ErrorBoundary';
import { useIdentity } from './hooks/useIdentity';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { setTokenProvider, setSubscriptionProvider } from './lib/api';
import { SubscriptionProviderRegistrar } from './components/SubscriptionProviderRegistrar';
import { useAppMode, AppMode } from './contexts/AppModeContext';
import SubscriptionPickerModal from './components/auth/SubscriptionPickerModal';

/**
 * Authentication Loading Component
 */
function AuthLoading() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating with Access Identity...</p>
      </div>
    </div>
  );
}

/**
 * Authentication Error Component
 */
function AuthError({ error, onRetry }: { error: Error; onRetry: () => void }) {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center max-w-md">
        <div className="text-destructive text-6xl mb-4">⚠️</div>
        <h2 className="text-2xl font-bold mb-2">Authentication Error</h2>
        <p className="text-muted-foreground mb-4">{error.message}</p>
        <button 
          onClick={onRetry}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Retry Authentication
        </button>
      </div>
    </div>
  );
}

/**
 * Subscription Loading Component
 * Shows loading screen while fetching subscriptions in Standalone mode
 */
function SubscriptionLoading() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading subscriptions...</p>
      </div>
    </div>
  );
}

/**
 * App Content Component
 * This component is rendered inside SubscriptionProvider and can access subscription context
 */
function AppContent({ instanceId }: { instanceId?: string }) {
  const { mode, isStandalone } = useAppMode();
  const subscription = useSubscription();

  // Standalone mode: Wait for subscription to be fetched
  if (isStandalone && subscription.loading) {
    return <SubscriptionLoading />;
  }

  // Standalone mode: Show error if subscription fetch failed
  if (isStandalone && subscription.error) {
    console.error('[App] Standalone mode - subscription fetch failed:', subscription.error);
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md">
          <div className="text-destructive text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-2">Subscription Error</h2>
          <p className="text-muted-foreground mb-4">{subscription.error}</p>
          <button 
            onClick={() => subscription.refetch()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Standalone mode: Show subscription picker if multiple subscriptions exist
  if (isStandalone && !subscription.subscriptionId && subscription.showSubscriptionPicker) {
    return (
      <div className="flex items-center justify-center h-screen">
        <SubscriptionPickerModal />
      </div>
    );
  }

  // Standalone mode: Ensure subscription is selected (no subscriptions available)
  if (isStandalone && !subscription.subscriptionId) {
    console.error('[App] Standalone mode - no subscription selected');
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md">
          <div className="text-destructive text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-2">No Subscription Available</h2>
          <p className="text-muted-foreground mb-4">
            You don't have access to any subscriptions. Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  // Note: Logging here is fine - only logs when component reaches this point (after all early returns pass)
  // This runs on every render in dev (StrictMode) but won't impact production performance

  // Render the main application routes
  return (
    <Routes>
      {/* Embed chat page - optimized for iframe embedding */}
      <Route 
        path="/embed/:workflowId" 
        element={
          <ErrorBoundary level="page">
            <EmbedChatPage />
          </ErrorBoundary>
        } 
      />
      
      {/* Main app routes (with RealTimeProvider for workflow editor) */}
      <Route 
          path="/*" 
          element={
            <RealTimeProvider>
              <DragDropProvider>
                <Routes>
                  <Route path="/" element={<AppLayout />}>
                    <Route index element={<Home />} />
                    <Route path="workflows" element={<WorkflowList />} />
                    <Route 
                      path="workflows/:id" 
                      element={
                        <ErrorBoundary level="page">
                          <WorkflowEditor />
                        </ErrorBoundary>
                      } 
                    />
                    <Route path="executions" element={<Executions />} />
                    <Route path="credentials" element={<Credentials />} />
                    <Route path="knowledge-base" element={<KnowledgeBase />} />
                    <Route path="data-tables" element={<DataTables />} />
                    <Route path="teams" element={<Teams />} />

                    <Route path="analytics" element={<Analytics />} />
                    <Route path="templates" element={<Templates />} />
                    <Route path="library-requests" element={<LibraryRequests />} />
                    <Route path="variables" element={<Variables />} />
                    <Route path="chat-demo" element={<ChatDemo />} />
                  </Route>
                </Routes>
              </DragDropProvider>
            </RealTimeProvider>
          }
      />
    </Routes>
  );
}

/**
 * Main App Component with Unified OIDC Authentication
 * 
 * Supports three deployment modes (all using real JWT authentication):
 * - NATIVE TAB MODE: Silent authentication (background) + subscription from ProductProvider
 * - STANDALONE MODE: Interactive authentication (redirects to login) + subscription from API
 * - E2B MODE: Interactive authentication (redirects to login) + subscription from API
 * 
 * All modes use Access Identity OIDC/JWT tokens validated by the backend.
 * E2B mode is treated the same as Standalone for authentication purposes.
 * 
 * @param instanceId - Optional instanceId from Native Tab mode (where ProductProvider is available)
 */
export default function App({ instanceId }: { instanceId?: string }) {
  const { clearWorkflow } = useWorkflowStore();
  const identity = useIdentity();
  const { mode, isNativeTab } = useAppMode();

  // Initialize property schemas on app startup
  useEffect(() => {
    initializePropertySchemas();
  }, []);

  // Register token provider with axios for automatic authentication
  // All modes (Native Tab, Standalone, E2B) use real JWT tokens
  useEffect(() => {
    if (identity.isAuthenticated && identity.getAccessToken) {
      setTokenProvider(identity.getAccessToken);
    }
  }, [identity.isAuthenticated, identity.getAccessToken, mode]);

  // Clear workflow state on page refresh/close to free memory
  useEffect(() => {
    const handleBeforeUnload = () => {
      clearWorkflow();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [clearWorkflow]);

  // Log authentication only once when authenticated (must be BEFORE early returns to avoid Rules of Hooks violation)
  useEffect(() => {
    // User authenticated
  }, [identity.isAuthenticated, identity.user?.profile?.email, identity.user?.profile?.sub]);

  // Authentication trigger effect
  useEffect(() => {
    // Trigger authentication if needed
    if (!identity.isAuthenticated && !identity.loading && !identity.error) {
      if (isNativeTab) {
        identity.signin();
      } else {
        // Standalone & E2B modes use interactive redirect signin
        identity.signinRedirect();
      }
    }
  }, [identity.isAuthenticated, identity.loading, identity.error, mode, isNativeTab, identity.signin, identity.signinRedirect]);

  // Request reauthentication on login_required error (Native Tab mode only)
  useEffect(() => {
    if (identity.error && identity.error.message === 'login_required' && isNativeTab) {
      try {
        const message = {
          type: "RequestReauthentication"
        };
        // Post to parent window (Access Workspace)
        window.parent.postMessage(JSON.stringify(message), '*'); // Use wildcard for now, will be restricted later
      } catch (error) {
        console.error('[App] Failed to request reauthentication:', error);
      }
    }
  }, [identity.error, isNativeTab]);

  // Show loading state during authentication
  if (identity.loading || !identity.isAuthenticated) {
    return <AuthLoading />;
  }

  // Show error state with retry option
  if (identity.error) {
    const handleRetry = () => {
      window.location.reload();
    };
    return <AuthError error={identity.error} onRetry={handleRetry} />;
  }

  // Render the main application (BrowserRouter is provided by AppContainer)
  // SubscriptionProvider wraps AppContent so it can access subscription context
  return (
    <ErrorBoundary level="root">
      <SubscriptionProvider instanceId={instanceId}>
        <SubscriptionProviderRegistrar />
        <AppContent instanceId={instanceId} />
      </SubscriptionProvider>
    </ErrorBoundary>
  );
}
